import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/api/query-keys";
import { api } from "@/lib/api";
import type {
	TenantApartment,
	TenantChartDataPoint,
	TenantChartParams,
} from "./types";

type RawChartPoint = { dt: string; sum: number };

function mapChartPoints(raw: RawChartPoint[]): TenantChartDataPoint[] {
	return raw.map(({ dt, sum }) => ({ date: dt, value: sum }));
}

export function useTenantApartments(options?: { enabled?: boolean }) {
	return useQuery({
		queryKey: queryKeys.tenant.apartments(),
		queryFn: async (): Promise<TenantApartment[]> => {
			const res = await api.get<TenantApartment[]>("/api/tenant/apartment/");
			return res.data;
		},
		enabled: options?.enabled,
	});
}

export function useTenantApartmentById(id: number) {
	return useQuery({
		queryKey: queryKeys.tenant.apartments(),
		queryFn: async (): Promise<TenantApartment[]> => {
			const res = await api.get<TenantApartment[]>("/api/tenant/apartment/");
			return res.data;
		},
		select: (apartments) => apartments.find((a) => a.id === id),
	});
}

export function useTenantWaterChart(params: TenantChartParams = {}) {
	return useQuery({
		queryKey: queryKeys.tenant.waterChart(params),
		queryFn: async (): Promise<TenantChartDataPoint[]> => {
			const res = await api.get<RawChartPoint[]>("/api/tenant/water_chart/", {
				params,
			});
			return mapChartPoints(res.data);
		},
	});
}

export function useTenantHeatChart(params: TenantChartParams = {}) {
	return useQuery({
		queryKey: queryKeys.tenant.heatChart(params),
		queryFn: async (): Promise<TenantChartDataPoint[]> => {
			const res = await api.get<RawChartPoint[]>("/api/tenant/heat_chart/", {
				params,
			});
			return mapChartPoints(res.data);
		},
	});
}

export function useTenantElectricityChart(params: TenantChartParams = {}) {
	return useQuery({
		queryKey: queryKeys.tenant.electricityChart(params),
		queryFn: async (): Promise<TenantChartDataPoint[]> => {
			const res = await api.get<RawChartPoint[]>(
				"/api/tenant/electricity_chart/",
				{ params },
			);
			return mapChartPoints(res.data);
		},
	});
}
